import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Label,
  ChoiceRadio,
  ChoiceRadioItem,
  useIsMobile,
} from '@design-system';

import { useInviteClientStore } from '@/features/invite-client';
import { SelectClientRelationship } from '@/shared/components/select-client-relationship';
import { SelectReferralSource } from '@/shared/components/select-referral-source';

import { Settings } from 'lucide-react';

export function AdditionalSettingsContent() {
  const { inviteClientConfig, handleConfigChange } = useInviteClientStore();

  const { shareResults, clientRelationship, referralSource } = inviteClientConfig;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">Should clients see their assessment results?</Label>
        <ChoiceRadio
          value={shareResults}
          onValueChange={value => handleConfigChange('shareResults', value)}
        >
          <ChoiceRadioItem
            value="yes"
            title="Yes, share immediately"
            description="Client receives results link after completing assessment"
          />
          <ChoiceRadioItem
            value="no"
            title="No, I'll review first"
            description="Review results before deciding what to share"
          />
        </ChoiceRadio>
      </div>

      <SelectClientRelationship
        value={clientRelationship}
        onChange={value => handleConfigChange('clientRelationship', value)}
      />

      <SelectReferralSource
        selectedReferralSourceId={referralSource}
        onChangeReferralSourceId={value => handleConfigChange('referralSource', value)}
      />
    </div>
  );
}

interface AdditionalSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdditionalSettingsDialog({ open, onOpenChange }: AdditionalSettingsDialogProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className="z-60 max-h-[85vh] overflow-y-auto px-4">
          <DrawerHeader className="text-left">
            <DrawerTitle>Additional Settings</DrawerTitle>
            <DrawerDescription>
              Configure how clients interact with their assessment
            </DrawerDescription>
          </DrawerHeader>
          <div className="py-4">
            <AdditionalSettingsContent />
          </div>
          <DrawerFooter className="px-0">
            <Button onClick={() => onOpenChange(false)}>Save</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="z-60 max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Additional Settings</DialogTitle>
          <DialogDescription>
            Configure how clients interact with their assessment
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <AdditionalSettingsContent />
        </div>
        <Button
          className="w-full"
          onClick={() => onOpenChange(false)}
        >
          Save
        </Button>
      </DialogContent>
    </Dialog>
  );
}

interface AdditionalSettingsButtonProps {
  className?: string;
}

export function AdditionalSettingsButton({ className }: AdditionalSettingsButtonProps) {
  const { showAdditionalSettings, setShowAdditionalSettings } = useInviteClientStore();

  return (
    <>
      <Button
        variant="outline"
        className={className}
        onClick={() => setShowAdditionalSettings(true)}
      >
        <Settings className="size-4" />
        Additional Settings
      </Button>
      <AdditionalSettingsDialog
        open={showAdditionalSettings}
        onOpenChange={setShowAdditionalSettings}
      />
    </>
  );
}
